/**
 * @fileoverview BUD-02: putting a page's bytes on a Blossom server.
 *
 * EVERY PAGE NEEDS THIS, WHICH WAS NOT TRUE BEFORE 2026-09-16. Until that
 * ruling a public markdown page was a NIP-23 long-form event carrying its body
 * inline, and it never touched a blob store at all. Every page is our own kind
 * now and every body is a blob, so markdown takes this path exactly as raw HTML
 * does. The only thing that still varies below is whether the bytes handed to
 * us are ciphertext, and that is the caller's business rather than this file's.
 *
 * WE UPLOAD TO SEVERAL SERVERS AND TREAT ONE SUCCESS AS ENOUGH. The index event
 * carries server HINTS rather than a URL, and the reader falls through them
 * verifying the hash each time, so a second copy is redundancy rather than a
 * second source of truth. Refusing to publish because the third server was down
 * would be strictness with nothing behind it.
 *
 * WE STILL VERIFY WHAT WE UPLOADED. The server hands back a descriptor naming a
 * hash; if that is not the hash we computed over the bytes we sent, the server
 * has stored something other than our page and the hint would send readers to
 * bytes that fail their own check. We hash locally and compare rather than
 * believing the descriptor, which is the same rule the read path follows in the
 * other direction.
 *
 * THE UPLOAD IS SIGNED BY THE AUTHOR, NOT BY US. BUD-01 authorisation is a kind
 * 24242 event the user's own signer produces, so the server sees the author's
 * key and we never hold one. That is also why an upload can fail with
 * `unauthorized` on our own file service while succeeding on public servers: it
 * runs an access check that an unenrolled key does not pass.
 */

import type { SignerInterface } from '@cloistr/auth/core';

import { sha256Hex } from './fetchBlob';


/** How long a single upload may take before the next server is tried. */
const UPLOAD_TIMEOUT_MS = 30_000;

/** How long the authorisation event stays valid. */
const AUTH_LIFETIME_S = 3_600;

export interface UploadOutcome {
  /** The blob's sha256, which becomes the `x` tag. */
  hash: string;
  /** Servers that accepted and returned the right hash. Become `server` tags. */
  servers: string[];
  /** Every server tried that did not, for an honest message to the author. */
  refused: string[];
}

/**
 * Upload bytes to each server in turn, returning which ones took it.
 *
 * `servers` empty in the result means nothing accepted it. The caller must
 * treat that as a failed publish rather than publishing an event whose `x` tag
 * points at bytes no server holds: that would produce a page that is
 * permanently unavailable and looks, to every reader, exactly like a page that
 * was deleted.
 */
export async function uploadBlob(
  bytes: ArrayBuffer,
  mimeType: string,
  servers: readonly string[],
  signer: SignerInterface,
  fetchImpl: typeof fetch = fetch
): Promise<UploadOutcome> {
  const hash = await sha256Hex(bytes);
  const authorization = await authorizationHeader(hash, signer);

  const accepted: string[] = [];
  const refused: string[] = [];

  for (const server of servers) {
    let url: string;
    try {
      url = new URL('/upload', server).toString();
    } catch {
      refused.push(server);
      continue;
    }

    try {
      const response = await fetchImpl(url, {
        method: 'PUT',
        body: bytes,
        headers: { Authorization: authorization, 'Content-Type': mimeType },
        signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS),
      });
      if (!response.ok) {
        refused.push(server);
        continue;
      }

      // The descriptor is the server's claim about what it stored. Believing it
      // is how a hint ends up pointing at bytes that fail the reader's check.
      const descriptor: unknown = await response.json();
      const stored =
        typeof descriptor === 'object' && descriptor !== null
          ? (descriptor as Record<string, unknown>).sha256
          : null;
      if (typeof stored === 'string' && stored.toLowerCase() === hash) {
        accepted.push(server);
      } else {
        refused.push(server);
      }
    } catch {
      refused.push(server);
    }
  }

  return { hash, servers: accepted, refused };
}

/**
 * The `Authorization: Nostr <base64>` header BUD-01 defines.
 *
 * The `x` tag binds the authorisation to THESE bytes, so an intercepted header
 * cannot be replayed to upload something else, and `expiration` bounds how long
 * it is worth intercepting.
 */
async function authorizationHeader(hash: string, signer: SignerInterface): Promise<string> {
  const event = await signer.signEvent({
    kind: 24242,
    created_at: Math.floor(Date.now() / 1000),
    content: 'Upload a page',
    tags: [
      ['t', 'upload'],
      ['x', hash],
      ['expiration', String(Math.floor(Date.now() / 1000) + AUTH_LIFETIME_S)],
    ],
    pubkey: await signer.getPublicKey(),
  });

  // btoa is byte-oriented and the content is ASCII here, but a title with an
  // accent in it would break it, so the JSON goes through UTF-8 first.
  const json = JSON.stringify(event);
  const utf8 = new TextEncoder().encode(json);
  let binary = '';
  for (const byte of utf8) binary += String.fromCharCode(byte);
  return `Nostr ${btoa(binary)}`;
}
